import { CANVAS_SPECS } from 'src/configs/previewConfig';
import { PreviewConstants } from 'src/constants';
/**
 * Loads an image with a timeout mechanism to prevent infinite loading.
 *
 * @param url - The URL of the image to load
 * @param timeoutMs - Timeout duration in milliseconds (defaults to 10000ms)
 * @returns Promise that resolves with the loaded HTMLImageElement
 * @throws Error if image fails to load or timeout occurs
 */
export const loadImage = (url: string, timeoutMs: number = 10000): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = PreviewConstants.CANVAS.CROSS_ORIGIN;
    const timeoutId = setTimeout(() => {
      img.src = '';
      reject(new Error(`Timeout loading image: ${url}`));
    }, timeoutMs);
    img.onload = () => {
      clearTimeout(timeoutId);
      resolve(img);
    };
    img.onerror = () => {
      clearTimeout(timeoutId);
      reject(new Error(`Failed to load image: ${url}`));
    };
    img.src = url;
  });
};

/**
 * Upscales an image from original image to required dimensions
 * Uses a gradual multi-step approach for better upscaling results.
 *
 * @param image - The HTMLImageElement to upscale (expected to be 1920x720 px)
 * @param upScaleWidth - The target width for upscaling
 * @param upScaleHeight - The target height for upscaling
 * @returns Promise that resolves to the data URL of the upscaled image
 * @throws Error if canvas context cannot be obtained
 */
export const upscaleImage = (
  image: HTMLImageElement,
  upScaleWidth: number,
  upScaleHeight: number,
): Promise<string> => {
  return new Promise((resolve, reject) => {
    try {
      let currentWidth = image.width;
      let currentHeight = image.height;

      // Create temporary canvas for incremental scaling
      const tempCanvas = document.createElement(PreviewConstants.CANVAS.ELEMENT);
      const tempCtx = tempCanvas.getContext(PreviewConstants.CANVAS.CONTEXT);

      if (!tempCtx) {
        reject(new Error('Failed to get canvas context for image upscaling'));
        return;
      }

      // Set image smoothing for better quality
      tempCtx.imageSmoothingEnabled = true;
      tempCtx.imageSmoothingQuality = 'high';

      // Initial draw of the source image
      tempCanvas.width = currentWidth;
      tempCanvas.height = currentHeight;
      tempCtx.drawImage(image, 0, 0, currentWidth, currentHeight);

      // Gradually scale up in steps (max 1.5x per step)
      while (currentWidth < upScaleWidth * 0.8 || currentHeight < upScaleHeight * 0.8) {
        const nextWidth = Math.min(currentWidth * 1.5, upScaleWidth);
        const nextHeight = Math.min(currentHeight * 1.5, upScaleHeight);

        const currentImageData = tempCtx.getImageData(0, 0, currentWidth, currentHeight);

        tempCanvas.width = nextWidth;
        tempCanvas.height = nextHeight;

        tempCtx.imageSmoothingEnabled = true;
        if ('imageSmoothingQuality' in tempCtx) {
          tempCtx.imageSmoothingQuality = 'high';
        }
        const tempImg = document.createElement('canvas');
        tempImg.width = currentWidth;
        tempImg.height = currentHeight;
        const tempImgCtx = tempImg.getContext(PreviewConstants.CANVAS.CONTEXT);
        if (!tempImgCtx) {
          reject(new Error('Failed to get context for temporary image'));
          return;
        }
        tempImgCtx.putImageData(currentImageData, 0, 0);

        tempCtx.drawImage(tempImg, 0, 0, nextWidth, nextHeight);
        currentWidth = nextWidth;
        currentHeight = nextHeight;
      }

      const finalCanvas = document.createElement(PreviewConstants.CANVAS.ELEMENT);
      finalCanvas.width = upScaleWidth;
      finalCanvas.height = upScaleHeight;

      const finalCtx = finalCanvas.getContext(PreviewConstants.CANVAS.CONTEXT);
      if (!finalCtx) {
        reject(new Error('Failed to get canvas context for final upscaling'));
        return;
      }

      finalCtx.imageSmoothingEnabled = true;
      if ('imageSmoothingQuality' in finalCtx) {
        finalCtx.imageSmoothingQuality = 'high';
      }
      finalCtx.drawImage(tempCanvas, 0, 0, upScaleWidth, upScaleHeight);

      resolve(finalCanvas.toDataURL(CANVAS_SPECS.imageFormat));
    } catch (error) {
      reject(error instanceof Error ? error : new Error('Error during image upscaling'));
    }
  });
};
